import React, { useState, useEffect } from 'react';
import { auth } from '../services/firebase';
import {
    listSubscriptionPlans,
    searchUser,
    getUserSubscription,
    getSubscriptionHistory,
    updateUserSubscription,
    cancelSubscription,
    reactivateSubscription,
    revokeBillingExempt,
    previewSubscriptionChange,
    formatCurrency,
    formatDate,
    type SubscriptionPlan,
    type SubscriptionDetails,
    type SubscriptionHistory,
    type SubscriptionPreview,
} from '../services/subscriptionAdminService';
import {
    ChangePlanModal,
    BillingExemptModal,
    CancelModal,
    ReactivateModal,
    HistoryModal,
} from './AdminSubscriptionModals';

type AdminRole = 'super_admin' | 'ops_manager' | 'support_agent' | null;

/**
 * Get the admin role from Firebase custom claims
 */
async function getAdminRole(): Promise<AdminRole> {
    const user = auth.currentUser;
    if (!user) return null;

    try {
        const idTokenResult = await user.getIdTokenResult();
        const role = idTokenResult.claims.adminRole as AdminRole;
        return role || null;
    } catch (error) {
        console.error('Failed to get admin role:', error);
        return null;
    }
}

interface AdminSubscriptionDashboardProps {
    onBack?: () => void;
    initialUserId?: string;
}

export const AdminSubscriptionDashboard: React.FC<AdminSubscriptionDashboardProps> = ({
    onBack,
    initialUserId,
}) => {
    // State
    const [view, setView] = useState<'search' | 'details'>('search');
    const [userId, setUserId] = useState(initialUserId || '');
    const [searchInput, setSearchInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [adminRole, setAdminRole] = useState<AdminRole>(null);
    const [roleLoading, setRoleLoading] = useState(true);

    // Data state
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
    const [history, setHistory] = useState<SubscriptionHistory | null>(null);
    const [preview, setPreview] = useState<SubscriptionPreview | null>(null);

    // Modal state
    const [showChangePlanModal, setShowChangePlanModal] = useState(false);
    const [showBillingExemptModal, setShowBillingExemptModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showReactivateModal, setShowReactivateModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    // Form state
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [adminNotes, setAdminNotes] = useState('');
    const [cancelImmediate, setCancelImmediate] = useState(false);
    const [cancelReason, setCancelReason] = useState('');

    // Load admin role on mount
    useEffect(() => {
        const loadRole = async () => {
            setRoleLoading(true);
            const role = await getAdminRole();
            setAdminRole(role);
            setRoleLoading(false);
        };
        loadRole();
    }, []);

    // Load subscription plans on mount
    useEffect(() => {
        loadPlans();
    }, []);

    // Load subscription details if userId provided
    useEffect(() => {
        if (initialUserId) {
            handleSearch(initialUserId);
        }
    }, [initialUserId]);

    const loadPlans = async () => {
        try {
            const plansData = await listSubscriptionPlans();
            setPlans(plansData.filter(p => p.active));
        } catch (err: any) {
            console.error('Failed to load plans:', err);
            setError(err.message);
        }
    };

    const handleSearch = async (searchUserId?: string) => {
        const targetUserId = searchUserId || searchInput.trim();
        if (!targetUserId) {
            setError('Please enter a user ID or email');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Detect if input is email or userId
            const isEmail = targetUserId.includes('@');
            let userIdToFetch = targetUserId;

            if (isEmail) {
                // Search by email to get userId
                console.log('[AdminDashboard] Searching for user by email:', targetUserId);
                const searchResult = await searchUser(targetUserId);
                console.log('[AdminDashboard] Search result:', searchResult);
                userIdToFetch = searchResult.userId;
            }

            // Fetch subscription details
            console.log('[AdminDashboard] Fetching subscription for userId:', userIdToFetch);
            const subData = await getUserSubscription(userIdToFetch);
            console.log('[AdminDashboard] Subscription data:', subData);
            setSubscription(subData);
            setUserId(userIdToFetch);
            setView('details');
        } catch (err: any) {
            console.error('[AdminDashboard] Search/fetch error:', err);
            setError(err.message || 'Failed to search user');
            setSubscription(null);
        } finally {
            setLoading(false);
        }
    };

    const handleLoadHistory = async () => {
        if (!userId) return;

        setLoading(true);
        try {
            const historyData = await getSubscriptionHistory(userId, 50);
            setHistory(historyData);
            setShowHistoryModal(true);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePlan = async () => {
        if (!userId || !selectedPlanId) {
            setError('Please select a plan');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await updateUserSubscription(userId, selectedPlanId, undefined, adminNotes);
            setSuccess('✅ Subscription plan updated successfully. Changes will take effect on next billing cycle.');
            setShowChangePlanModal(false);
            setSelectedPlanId('');
            setAdminNotes('');
            setPreview(null);
            await handleSearch(userId);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePreviewChange = async (planId: string) => {
        if (!userId || !planId) return;

        setLoading(true);
        try {
            const previewData = await previewSubscriptionChange(userId, planId);
            setPreview(previewData);
        } catch (err: any) {
            setError(err.message);
            setPreview(null);
        } finally {
            setLoading(false);
        }
    };

    const handleGrantBillingExempt = async () => {
        if (!userId || !adminNotes) {
            setError('Admin notes are required for billing exemption');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await updateUserSubscription(userId, undefined, true, adminNotes);
            setSuccess('✅ Billing exemption granted. User will have lifetime free access.');
            setShowBillingExemptModal(false);
            setAdminNotes('');
            await handleSearch(userId);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRevokeBillingExempt = async () => {
        if (!userId || !selectedPlanId || !adminNotes) {
            setError('Plan selection and admin notes are required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await revokeBillingExempt(userId, selectedPlanId, adminNotes);
            setSuccess('✅ Billing exemption revoked and paid subscription created.');
            setShowBillingExemptModal(false);
            setSelectedPlanId('');
            setAdminNotes('');
            await handleSearch(userId);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async () => {
        if (!userId) return;

        setLoading(true);
        setError(null);

        try {
            await cancelSubscription(userId, cancelImmediate, cancelReason);
            setSuccess(
                cancelImmediate
                    ? '✅ Subscription canceled immediately'
                    : '✅ Subscription will be canceled at period end'
            );
            setShowCancelModal(false);
            setCancelImmediate(false);
            setCancelReason('');
            await handleSearch(userId);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleReactivate = async () => {
        if (!userId) return;

        setLoading(true);
        setError(null);

        try {
            await reactivateSubscription(userId, selectedPlanId || undefined);
            setSuccess('✅ Subscription reactivated successfully');
            setShowReactivateModal(false);
            setSelectedPlanId('');
            await handleSearch(userId);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const renderSearchView = () => (
        <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Search User Subscription</h2>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            User ID or Email
                        </label>
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Enter user UID or email address"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                    </div>

                    <button
                        onClick={() => handleSearch()}
                        disabled={loading || !searchInput.trim()}
                        className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                        {loading ? 'Searching...' : 'Search'}
                    </button>
                </div>

                {error && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-800">{error}</p>
                    </div>
                )}
            </div>

            <div className="mt-8 bg-white rounded-lg shadow-md p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Available Subscription Plans</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {plans.map((plan) => (
                        <div key={plan.stripePriceId} className="border border-gray-200 rounded-lg p-4">
                            <h4 className="font-semibold text-lg text-gray-900">{plan.productName}</h4>
                            <p className="text-gray-600 text-sm mt-1">{plan.description}</p>
                            <div className="mt-3">
                                <span className="text-2xl font-bold text-blue-600">
                                    {formatCurrency(plan.amount, plan.currency)}
                                </span>
                                <span className="text-gray-600 text-sm">/{plan.interval}</span>
                            </div>
                            {plan.features && plan.features.length > 0 && (
                                <ul className="mt-3 space-y-1">
                                    {plan.features.map((feature, idx) => (
                                        <li key={idx} className="text-sm text-gray-700 flex items-start">
                                            <span className="text-green-500 mr-2">✓</span>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderDetailsView = () => {
        if (!subscription) return null;

        const { firestoreData, stripeData } = subscription;
        const isBillingExempt = firestoreData.billingExempt;
        const isCanceled = firestoreData.subscriptionStatus === 'canceled';
        const isCancelPending = firestoreData.cancelAtPeriodEnd;

        return (
            <div className="max-w-6xl mx-auto">
                <div className="mb-6 flex items-center justify-between">
                    <button
                        onClick={() => {
                            setView('search');
                            setSubscription(null);
                            setUserId('');
                            setError(null);
                            setSuccess(null);
                        }}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                        ← Back to Search
                    </button>
                    <button
                        onClick={handleLoadHistory}
                        className="text-gray-600 hover:text-gray-800 font-medium"
                    >
                        View History
                    </button>
                </div>

                {success && (
                    <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-green-800">{success}</p>
                    </div>
                )}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-800">{error}</p>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Subscription Details</h2>
                        {isBillingExempt && (
                            <span className="px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
                                🎁 Billing Exempt
                            </span>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Firestore Data</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Status:</span>
                                    <span className="font-medium text-gray-900">{firestoreData.subscriptionStatus}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Price ID:</span>
                                    <span className="font-mono text-xs text-gray-900">{firestoreData.stripePriceId || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Customer ID:</span>
                                    <span className="font-mono text-xs text-gray-900">{firestoreData.stripeCustomerId || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Billing Exempt:</span>
                                    <span className={firestoreData.billingExempt ? 'text-purple-600 font-semibold' : 'text-gray-900'}>
                                        {firestoreData.billingExempt ? 'Yes' : 'No'}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Cancel at Period End:</span>
                                    <span className={firestoreData.cancelAtPeriodEnd ? 'text-orange-600' : 'text-gray-900'}>
                                        {firestoreData.cancelAtPeriodEnd ? 'Yes' : 'No'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Stripe Data</h3>
                            {stripeData ? (
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Subscription ID:</span>
                                        <span className="font-mono text-xs text-gray-900">{stripeData.id}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Status:</span>
                                        <span className="font-medium text-gray-900">{stripeData.status}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Amount:</span>
                                        <span className="font-semibold text-gray-900">
                                            {formatCurrency(stripeData.amount)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Current Period:</span>
                                        <span className="text-gray-900">{formatDate(stripeData.currentPeriodStart)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Next Billing:</span>
                                        <span className="text-gray-900">{formatDate(stripeData.currentPeriodEnd)}</span>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No active Stripe subscription</p>
                            )}
                        </div>
                    </div>

                    <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {!isBillingExempt && !isCanceled && (
                            <button
                                onClick={() => setShowChangePlanModal(true)}
                                disabled={!canModify}
                                className="bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                title={!canModify ? 'Super admin role required' : ''}
                            >
                                Change Plan
                            </button>
                        )}

                        {!isBillingExempt ? (
                            <button
                                onClick={() => setShowBillingExemptModal(true)}
                                disabled={!canModify}
                                className="bg-purple-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                title={!canModify ? 'Super admin role required' : ''}
                            >
                                Grant Exempt
                            </button>
                        ) : (
                            <button
                                onClick={() => setShowBillingExemptModal(true)}
                                disabled={!canModify}
                                className="bg-orange-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                title={!canModify ? 'Super admin role required' : ''}
                            >
                                Revoke Exempt
                            </button>
                        )}

                        {!isCanceled && !isCancelPending && !isBillingExempt && (
                            <button
                                onClick={() => setShowCancelModal(true)}
                                disabled={!canModify}
                                className="bg-red-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                title={!canModify ? 'Super admin role required' : ''}
                            >
                                Cancel
                            </button>
                        )}

                        {(isCanceled || isCancelPending) && (
                            <button
                                onClick={() => setShowReactivateModal(true)}
                                disabled={!canModify}
                                className="bg-green-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                title={!canModify ? 'Super admin role required' : ''}
                            >
                                Reactivate
                            </button>
                        )}
                    </div>

                    {!canModify && (
                        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <p className="text-sm text-yellow-800">
                                ⚠️ <strong>View-Only Mode:</strong> You can view subscription details but cannot make modifications. Super admin role required for changes.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Determine if user can modify subscriptions
    const canModify = adminRole === 'super_admin';
    const canView = adminRole === 'super_admin' || adminRole === 'ops_manager';

    // Show loading state while checking role
    if (roleLoading) {
        return (
            <div className="min-h-screen bg-gray-50 py-8 px-4 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Verifying permissions...</p>
                </div>
            </div>
        );
    }

    // Show access denied for unauthorized roles
    if (!canView) {
        return (
            <div className="min-h-screen bg-gray-50 py-8 px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
                        <div className="text-6xl mb-4">🚫</div>
                        <h2 className="text-2xl font-bold text-red-900 mb-2">Access Denied</h2>
                        <p className="text-red-700 mb-6">
                            You do not have permission to access subscription management.
                            {adminRole ? ` Your role: ${adminRole}` : ' No admin role assigned.'}
                        </p>
                        <p className="text-sm text-gray-600 mb-6">
                            Required role: <strong>super_admin</strong> or <strong>ops_manager</strong>
                        </p>
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="bg-blue-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                            >
                                ← Back to Dashboard
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Navigation Header */}
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    <span className="font-medium">Back</span>
                                </button>
                            )}
                            <nav className="flex items-center space-x-2 text-sm">
                                <span className="text-gray-500">Admin Portal</span>
                                <span className="text-gray-400">/</span>
                                <span className="text-gray-900 font-medium">Subscription Management</span>
                            </nav>
                        </div>
                        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            {adminRole === 'super_admin' ? '👑 Super Admin' : '👀 View Only'}
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
                            <p className="text-gray-600 mt-2">
                                Manage user subscriptions, billing exemptions, and view modification history
                            </p>
                        </div>
                        {!canModify && (
                            <div className="text-right">
                                <p className="text-xs text-gray-500">Modification disabled</p>
                            </div>
                        )}
                    </div>
                </div>

                {view === 'search' ? renderSearchView() : renderDetailsView()}

                <ChangePlanModal
                    isOpen={showChangePlanModal}
                    onClose={() => {
                        setShowChangePlanModal(false);
                        setSelectedPlanId('');
                        setAdminNotes('');
                        setPreview(null);
                    }}
                    plans={plans}
                    selectedPlanId={selectedPlanId}
                    onSelectPlan={setSelectedPlanId}
                    adminNotes={adminNotes}
                    onNotesChange={setAdminNotes}
                    preview={preview}
                    onPreview={handlePreviewChange}
                    onConfirm={handleChangePlan}
                    loading={loading}
                />

                <BillingExemptModal
                    isOpen={showBillingExemptModal}
                    onClose={() => {
                        setShowBillingExemptModal(false);
                        setSelectedPlanId('');
                        setAdminNotes('');
                    }}
                    isCurrentlyExempt={subscription?.firestoreData.billingExempt || false}
                    plans={plans}
                    selectedPlanId={selectedPlanId}
                    onSelectPlan={setSelectedPlanId}
                    adminNotes={adminNotes}
                    onNotesChange={setAdminNotes}
                    onConfirm={
                        subscription?.firestoreData.billingExempt
                            ? handleRevokeBillingExempt
                            : handleGrantBillingExempt
                    }
                    loading={loading}
                />

                <CancelModal
                    isOpen={showCancelModal}
                    onClose={() => {
                        setShowCancelModal(false);
                        setCancelImmediate(false);
                        setCancelReason('');
                    }}
                    immediate={cancelImmediate}
                    onImmediateChange={setCancelImmediate}
                    reason={cancelReason}
                    onReasonChange={setCancelReason}
                    onConfirm={handleCancel}
                    loading={loading}
                    currentPeriodEnd={subscription?.stripeData?.currentPeriodEnd}
                />

                <ReactivateModal
                    isOpen={showReactivateModal}
                    onClose={() => {
                        setShowReactivateModal(false);
                        setSelectedPlanId('');
                    }}
                    plans={plans}
                    selectedPlanId={selectedPlanId}
                    onSelectPlan={setSelectedPlanId}
                    onConfirm={handleReactivate}
                    loading={loading}
                    needsNewPlan={subscription?.firestoreData.subscriptionStatus === 'canceled'}
                />

                <HistoryModal
                    isOpen={showHistoryModal}
                    onClose={() => {
                        setShowHistoryModal(false);
                        setHistory(null);
                    }}
                    history={history}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default AdminSubscriptionDashboard;
